import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useResolvedDaycareHotelClinicId } from "@/hooks/useResolvedDaycareHotelClinicId";

type StayScheduleDefaultsRow = {
  id: string;
  daycare_check_in_from_time: string | null;
  daycare_check_in_to_time: string | null;
  daycare_check_out_from_time: string | null;
  daycare_check_out_to_time: string | null;
  hotel_check_in_from_time: string | null;
  hotel_check_in_to_time: string | null;
  hotel_check_out_from_time: string | null;
  hotel_check_out_to_time: string | null;
  daycare_shift_mode?: string | null;
  hotel_shift_mode?: string | null;
  daycare_evening_check_in_from_time?: string | null;
  daycare_evening_check_in_to_time?: string | null;
  daycare_evening_check_out_from_time?: string | null;
  daycare_evening_check_out_to_time?: string | null;
  hotel_evening_check_in_from_time?: string | null;
  hotel_evening_check_in_to_time?: string | null;
  hotel_evening_check_out_from_time?: string | null;
  hotel_evening_check_out_to_time?: string | null;
};

/** `single` = uma janela só; `two` = manhã e noite, e a reserva pergunta o turno. */
export type StayShiftMode = "single" | "two";

export interface StayScheduleDefaults {
  id: string | null;
  daycareCheckInFrom: string;
  daycareCheckInTo: string;
  daycareCheckOutFrom: string;
  daycareCheckOutTo: string;
  hotelCheckInFrom: string;
  hotelCheckInTo: string;
  hotelCheckOutFrom: string;
  hotelCheckOutTo: string;
  daycareShiftMode: StayShiftMode;
  hotelShiftMode: StayShiftMode;
  daycareEveningCheckInFrom: string;
  daycareEveningCheckInTo: string;
  daycareEveningCheckOutFrom: string;
  daycareEveningCheckOutTo: string;
  hotelEveningCheckInFrom: string;
  hotelEveningCheckInTo: string;
  hotelEveningCheckOutFrom: string;
  hotelEveningCheckOutTo: string;
}

export interface StayScheduleDefaultsInput {
  daycareCheckInFrom?: string | null;
  daycareCheckInTo?: string | null;
  daycareCheckOutFrom?: string | null;
  daycareCheckOutTo?: string | null;
  hotelCheckInFrom?: string | null;
  hotelCheckInTo?: string | null;
  hotelCheckOutFrom?: string | null;
  hotelCheckOutTo?: string | null;
  daycareShiftMode?: StayShiftMode;
  hotelShiftMode?: StayShiftMode;
  daycareEveningCheckInFrom?: string | null;
  daycareEveningCheckInTo?: string | null;
  daycareEveningCheckOutFrom?: string | null;
  daycareEveningCheckOutTo?: string | null;
  hotelEveningCheckInFrom?: string | null;
  hotelEveningCheckInTo?: string | null;
  hotelEveningCheckOutFrom?: string | null;
  hotelEveningCheckOutTo?: string | null;
}

export const EMPTY_STAY_SCHEDULE_DEFAULTS: StayScheduleDefaults = {
  id: null,
  daycareCheckInFrom: "08:00",
  daycareCheckInTo: "08:00",
  daycareCheckOutFrom: "18:00",
  daycareCheckOutTo: "18:00",
  hotelCheckInFrom: "08:00",
  hotelCheckInTo: "08:00",
  hotelCheckOutFrom: "18:00",
  hotelCheckOutTo: "18:00",
  daycareShiftMode: "single",
  hotelShiftMode: "single",
  daycareEveningCheckInFrom: "17:00",
  daycareEveningCheckInTo: "19:00",
  daycareEveningCheckOutFrom: "17:00",
  daycareEveningCheckOutTo: "19:00",
  hotelEveningCheckInFrom: "17:00",
  hotelEveningCheckInTo: "19:00",
  hotelEveningCheckOutFrom: "17:00",
  hotelEveningCheckOutTo: "19:00",
};

const shiftMode = (value: string | null | undefined): StayShiftMode => (value === "two" ? "two" : "single");

const toHourMinute = (value: string | null) => value?.slice(0, 5) ?? null;
const withFallback = (value: string | null, fallback: string) => value ?? fallback;

export function normalizeStayScheduleDefaults(row: StayScheduleDefaultsRow | null): StayScheduleDefaults {
  if (!row) return EMPTY_STAY_SCHEDULE_DEFAULTS;
  return {
    id: row.id,
    daycareCheckInFrom: withFallback(toHourMinute(row.daycare_check_in_from_time), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareCheckInFrom),
    daycareCheckInTo: withFallback(toHourMinute(row.daycare_check_in_to_time), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareCheckInTo),
    daycareCheckOutFrom: withFallback(toHourMinute(row.daycare_check_out_from_time), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareCheckOutFrom),
    daycareCheckOutTo: withFallback(toHourMinute(row.daycare_check_out_to_time), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareCheckOutTo),
    hotelCheckInFrom: withFallback(toHourMinute(row.hotel_check_in_from_time), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelCheckInFrom),
    hotelCheckInTo: withFallback(toHourMinute(row.hotel_check_in_to_time), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelCheckInTo),
    hotelCheckOutFrom: withFallback(toHourMinute(row.hotel_check_out_from_time), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelCheckOutFrom),
    hotelCheckOutTo: withFallback(toHourMinute(row.hotel_check_out_to_time), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelCheckOutTo),
    daycareShiftMode: shiftMode(row.daycare_shift_mode),
    hotelShiftMode: shiftMode(row.hotel_shift_mode),
    daycareEveningCheckInFrom: withFallback(toHourMinute(row.daycare_evening_check_in_from_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareEveningCheckInFrom),
    daycareEveningCheckInTo: withFallback(toHourMinute(row.daycare_evening_check_in_to_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareEveningCheckInTo),
    daycareEveningCheckOutFrom: withFallback(toHourMinute(row.daycare_evening_check_out_from_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareEveningCheckOutFrom),
    daycareEveningCheckOutTo: withFallback(toHourMinute(row.daycare_evening_check_out_to_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.daycareEveningCheckOutTo),
    hotelEveningCheckInFrom: withFallback(toHourMinute(row.hotel_evening_check_in_from_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelEveningCheckInFrom),
    hotelEveningCheckInTo: withFallback(toHourMinute(row.hotel_evening_check_in_to_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelEveningCheckInTo),
    hotelEveningCheckOutFrom: withFallback(toHourMinute(row.hotel_evening_check_out_from_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelEveningCheckOutFrom),
    hotelEveningCheckOutTo: withFallback(toHourMinute(row.hotel_evening_check_out_to_time ?? null), EMPTY_STAY_SCHEDULE_DEFAULTS.hotelEveningCheckOutTo),
  };
}

export const stayScheduleDefaultsKey = (clinicId: string | null) => ["stay-schedule-defaults", clinicId];

export function useStayScheduleDefaults() {
  const { clinicId, isLoading: isLoadingClinicId } = useResolvedDaycareHotelClinicId();

  return useQuery({
    queryKey: stayScheduleDefaultsKey(clinicId),
    queryFn: async (): Promise<StayScheduleDefaults> => {
      if (!clinicId) return EMPTY_STAY_SCHEDULE_DEFAULTS;
      const result = await supabase
        .from("stay_schedule_defaults" as never)
        .select(
          "id, daycare_check_in_from_time, daycare_check_in_to_time, daycare_check_out_from_time, daycare_check_out_to_time, " +
          "hotel_check_in_from_time, hotel_check_in_to_time, hotel_check_out_from_time, hotel_check_out_to_time, " +
          "daycare_shift_mode, hotel_shift_mode, " +
          "daycare_evening_check_in_from_time, daycare_evening_check_in_to_time, daycare_evening_check_out_from_time, daycare_evening_check_out_to_time, " +
          "hotel_evening_check_in_from_time, hotel_evening_check_in_to_time, hotel_evening_check_out_from_time, hotel_evening_check_out_to_time",
        )
        .eq("clinic_id", clinicId)
        .maybeSingle() as unknown as { data: StayScheduleDefaultsRow | null; error: unknown };
      if (result.error) throw result.error;
      return normalizeStayScheduleDefaults(result.data);
    },
    enabled: !!clinicId && !isLoadingClinicId,
  });
}

export function useSaveStayScheduleDefaults() {
  const queryClient = useQueryClient();
  const { clinicId } = useResolvedDaycareHotelClinicId();

  return useMutation({
    mutationFn: async (input: StayScheduleDefaultsInput) => {
      if (!clinicId) throw new Error("no_clinic_selected");
      const payload = {
        clinic_id: clinicId,
        ...(input.daycareCheckInFrom !== undefined ? { daycare_check_in_from_time: input.daycareCheckInFrom ? `${input.daycareCheckInFrom}:00` : null } : {}),
        ...(input.daycareCheckInTo !== undefined ? { daycare_check_in_to_time: input.daycareCheckInTo ? `${input.daycareCheckInTo}:00` : null } : {}),
        ...(input.daycareCheckOutFrom !== undefined ? { daycare_check_out_from_time: input.daycareCheckOutFrom ? `${input.daycareCheckOutFrom}:00` : null } : {}),
        ...(input.daycareCheckOutTo !== undefined ? { daycare_check_out_to_time: input.daycareCheckOutTo ? `${input.daycareCheckOutTo}:00` : null } : {}),
        ...(input.hotelCheckInFrom !== undefined ? { hotel_check_in_from_time: input.hotelCheckInFrom ? `${input.hotelCheckInFrom}:00` : null } : {}),
        ...(input.hotelCheckInTo !== undefined ? { hotel_check_in_to_time: input.hotelCheckInTo ? `${input.hotelCheckInTo}:00` : null } : {}),
        ...(input.hotelCheckOutFrom !== undefined ? { hotel_check_out_from_time: input.hotelCheckOutFrom ? `${input.hotelCheckOutFrom}:00` : null } : {}),
        ...(input.hotelCheckOutTo !== undefined ? { hotel_check_out_to_time: input.hotelCheckOutTo ? `${input.hotelCheckOutTo}:00` : null } : {}),
        ...(input.daycareShiftMode !== undefined ? { daycare_shift_mode: input.daycareShiftMode } : {}),
        ...(input.hotelShiftMode !== undefined ? { hotel_shift_mode: input.hotelShiftMode } : {}),
        ...(input.daycareEveningCheckInFrom !== undefined ? { daycare_evening_check_in_from_time: input.daycareEveningCheckInFrom ? `${input.daycareEveningCheckInFrom}:00` : null } : {}),
        ...(input.daycareEveningCheckInTo !== undefined ? { daycare_evening_check_in_to_time: input.daycareEveningCheckInTo ? `${input.daycareEveningCheckInTo}:00` : null } : {}),
        ...(input.daycareEveningCheckOutFrom !== undefined ? { daycare_evening_check_out_from_time: input.daycareEveningCheckOutFrom ? `${input.daycareEveningCheckOutFrom}:00` : null } : {}),
        ...(input.daycareEveningCheckOutTo !== undefined ? { daycare_evening_check_out_to_time: input.daycareEveningCheckOutTo ? `${input.daycareEveningCheckOutTo}:00` : null } : {}),
        ...(input.hotelEveningCheckInFrom !== undefined ? { hotel_evening_check_in_from_time: input.hotelEveningCheckInFrom ? `${input.hotelEveningCheckInFrom}:00` : null } : {}),
        ...(input.hotelEveningCheckInTo !== undefined ? { hotel_evening_check_in_to_time: input.hotelEveningCheckInTo ? `${input.hotelEveningCheckInTo}:00` : null } : {}),
        ...(input.hotelEveningCheckOutFrom !== undefined ? { hotel_evening_check_out_from_time: input.hotelEveningCheckOutFrom ? `${input.hotelEveningCheckOutFrom}:00` : null } : {}),
        ...(input.hotelEveningCheckOutTo !== undefined ? { hotel_evening_check_out_to_time: input.hotelEveningCheckOutTo ? `${input.hotelEveningCheckOutTo}:00` : null } : {}),
      };
      const { error } = await (supabase.from("stay_schedule_defaults" as never) as any).upsert(payload, { onConflict: "clinic_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stayScheduleDefaultsKey(clinicId) });
    },
  });
}
